import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { all_routes } from '../../router/all_routes';
import Table from "../../../core/common/dataTable/index";
import { useDispatch, useSelector } from 'react-redux';
import { getIdCardData } from '../../../core/data/redux/idCardSlice';
import type { AppDispatch, RootState } from '../../../core/data/redux/store';
import { Spin } from 'antd';

interface DependentRequest {
    id: number;
    requestType: string;
    dependentName: string;
    relationship: string;
    appliedOn: string;
    status: string;
}
const DependentManagementList = () => {
    const routes = all_routes;
    const navigate = useNavigate();
    const [searchText, setSearchText] = useState("");

    const dispatch = useDispatch<AppDispatch>();
    const idCardState = useSelector((state: RootState) => state.idCardState);
    const loading = idCardState.loading === 'pending';
    const data = idCardState.data?.data || [];

    React.useEffect(() => {
        dispatch(getIdCardData({}));
    }, [dispatch]);

    const columns = [
        {
            title: "Sr. No.",
            dataIndex: "id",
            width: 80,
            render: (text: string, record: DependentRequest, index: number) => index + 1,
        },
        {
            title: "Request Type",
            dataIndex: "requestType",
            sorter: (a: DependentRequest, b: DependentRequest) => a.requestType.localeCompare(b.requestType),
        },
        {
            title: "Dependent Name",
            dataIndex: "dependentName",
            sorter: (a: DependentRequest, b: DependentRequest) => a.dependentName.localeCompare(b.dependentName),
        },
        {
            title: "Relationship",
            dataIndex: "relationship",
        },
        {
            title: "Applied On",
            dataIndex: "appliedOn",
            sorter: (a: DependentRequest, b: DependentRequest) => new Date(a.appliedOn).getTime() - new Date(b.appliedOn).getTime(),
        },
        {
            title: "Status",
            dataIndex: "status",
            render: (status: string) => (
                <span className={`badge rounded-pill ${status === 'Approved' ? 'bg-success-transparent text-success' : status === 'Rejected' ? 'bg-danger-transparent text-danger' : 'bg-warning-transparent text-warning'}`}>
                    {status}
                </span>
            ),
        },
        {
            title: "Actions",
            dataIndex: "actions",
            width: 100,
            render: () => (
                <div className="action-icon d-inline-flex" style={{ gap: 10 }}>
                    <Link to="#" className="btn btn-sm btn-info-transparent">
                        <i className="ti ti-eye"></i>
                    </Link>
                </div>
            ),
        },
    ];

    return (
        <div className="page-wrapper" style={{ backgroundColor: '#f4f7fb' }}>
            <div className="content">
                {/* Page Header */}
                <div className="d-md-flex d-block align-items-center justify-content-between mb-4">
                    <div className="my-auto mb-2">
                        <h3 className="page-title mb-1 fw-bold text-dark">Inclusion and Deletion of Dependents</h3>
                        <nav>
                            <ol className="breadcrumb mb-0">
                                <li className="breadcrumb-item">
                                    <Link to={routes.adminDashboard} className="text-secondary text-decoration-none">Home</Link>
                                </li>
                                <li className="breadcrumb-item">
                                    <Link to={routes.services.list} className="text-secondary text-decoration-none">Services</Link>
                                </li>
                                <li className="breadcrumb-item active" aria-current="page" style={{ color: '#c82333' }}>
                                    Inclusion & Deletion
                                </li>
                            </ol>
                        </nav>
                    </div>
                    <div className="mb-2">
                        <button
                            className="btn btn-danger d-flex align-items-center fw-bold px-4 py-2 rounded-3"
                            style={{ backgroundColor: '#c82333', borderColor: '#c82333' }}
                            onClick={() => navigate(routes.idCard.createFamilyMemberRequest)}
                        >
                            <i className="ti ti-plus me-2"></i> Create New Request
                        </button>
                    </div>
                </div>

                {/* Table Section */}
                <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
                    <div className="card-header bg-white border-bottom-0 pt-4 px-4 pb-0">
                        <div className="row align-items-center">
                            <div className="col-sm-6 col-md-4 mb-3">
                                <div className="input-group search-input border rounded-3 p-1">
                                    <span className="input-group-text bg-transparent border-0">
                                        <i className="ti ti-search text-muted"></i>
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control bg-transparent border-0 ps-0"
                                        placeholder="Search by name, relation or status..."
                                        value={searchText}
                                        onChange={(e) => setSearchText(e.target.value)}
                                        style={{ fontSize: '14px' }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="card-body p-0">
                        {loading ? (
                            <div className="text-center py-5">
                                <Spin size="large" />
                                <p className="mt-3 text-muted">Loading requests...</p>
                            </div>
                        ) : data.length > 0 ? (
                            <Table
                                columns={columns}
                                dataSource={data}
                                paginationConfig={{
                                    pageSize: 10,
                                    current: 1
                                }}
                            />
                        ) : (
                            <div className="text-center py-5">
                                <div className="avatar avatar-xxl bg-danger-transparent rounded-circle mb-3 mx-auto">
                                    <i className="ti ti-users-minus fs-1 text-danger opacity-50"></i>
                                </div>
                                <h4 className="fw-bold text-dark">No Records Found</h4>
                                <p className="text-muted mb-4 px-3">You have not submitted any inclusion or deletion requests yet.</p>
                                <button
                                    className="btn btn-danger px-4 fw-bold rounded-3"
                                    onClick={() => navigate(routes.idCard.createFamilyMemberRequest)}
                                    style={{ backgroundColor: '#c82333', borderColor: '#c82333' }}
                                >
                                    Start Application
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <style>{`
                .bg-success-transparent { background-color: rgba(40, 167, 69, 0.1); }
                .bg-danger-transparent { background-color: rgba(200, 35, 51, 0.1); }
                .bg-warning-transparent { background-color: rgba(255, 193, 7, 0.1); }
                .bg-info-transparent { background-color: rgba(23, 162, 184, 0.1); }
                .btn-info-transparent { color: #17a2b8; background-color: rgba(23, 162, 184, 0.1); border: none; }
                .btn-info-transparent:hover { background-color: #17a2b8; color: #fff; }
                .search-input .form-control:focus { box-shadow: none; }
                .card.rounded-4 { border-radius: 1rem !important; }
            `}</style>
        </div>
    );
};

export default DependentManagementList;
